import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { Card, Empty, Btn } from "../../components/UI";
import { AlertTriangle, Activity, CheckCircle2, Pill, CalendarPlus } from "lucide-react";

export default function MetabolicMonitoring({ client }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scheduling, setScheduling] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/clients/${client.id}/metabolic-monitoring`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (client?.id) load(); }, [client?.id]); // eslint-disable-line

  const autoschedule = async () => {
    setScheduling(true);
    try {
      const r = await api.post(`/clients/${client.id}/metabolic-monitoring/autoschedule`);
      const d = r.data;
      if (d.created > 0) {
        toast.success(`Scheduled ${d.created} task${d.created === 1 ? "" : "s"} for missing metrics. ${d.skipped ? `(${d.skipped} already open.)` : ""}`);
      } else if (d.skipped_reason) {
        toast.info(d.skipped_reason);
      } else {
        toast.info("All overdue metrics already have open tasks.");
      }
    } catch (err) { toast.error(apiError(err)); }
    finally { setScheduling(false); }
  };

  if (loading) return <div className="h-40 animate-pulse rounded-2xl bg-[#EFEBE9]" />;
  if (!data) return <Empty text="Metabolic data unavailable" />;

  return (
    <div className="space-y-4" data-testid="metabolic-monitoring-panel">
      <div className={`rounded-2xl border p-4 ${data.on_watchlist ? "border-[#B26A00] bg-[#FFF8E1]" : "border-[#E5D3D0] bg-[#FDFCF8]"}`}>
        <div className="flex items-center gap-2">
          {data.on_watchlist ? <AlertTriangle size={16} className="text-[#8D5A00]" /> : <CheckCircle2 size={16} className="text-[#2E7D32]" />}
          <div className="font-heading font-bold text-[#3E2723]">
            {data.on_watchlist ? "On Metabolic Watchlist" : "Not on Metabolic Watchlist"}
          </div>
        </div>
        <div className="text-xs text-[#5D4037] mt-1">
          {data.on_watchlist
            ? "This client is on one or more antipsychotics or mood stabilisers. APA and ADA recommend baseline plus quarterly weight and BMI, semi-annual blood pressure and HbA1c during titration, and an annual lipid panel."
            : "No qualifying medications on file. This dashboard activates automatically if a watch-listed medication is prescribed."}
        </div>
      </div>

      {data.watch_medications?.length > 0 && (
        <Card className="p-4" data-testid="metabolic-watch-meds">
          <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase text-[#7B6157]">
            <Pill size={12} /> Watch-listed Medications on Active List
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {data.watch_medications.map((m) => (
              <div key={m.id} className="rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] p-3">
                <div className="font-bold text-[#3E2723]">{m.medication}</div>
                {m.start_date && <div className="text-[11px] text-[#7B6157]">Started {dayjs(m.start_date).format("MMM D, YYYY")}</div>}
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
        <Card className="p-4">
          <div className="text-[10px] font-bold uppercase text-[#7B6157]">Latest Weight and BMI</div>
          {data.latest_weight ? (
            <>
              <div className="font-heading text-2xl font-bold text-[#3E2723]">
                {data.latest_weight.weight_lbs} lbs
              </div>
              <div className="text-xs text-[#7B6157]">BMI {data.latest_weight.bmi ?? "-"}</div>
              <div className="text-[10px] text-[#7B6157] mt-1">Recorded {dayjs(data.latest_weight.date).format("MMM D, YYYY")}</div>
            </>
          ) : (
            <div className="text-xs italic text-[#8D5A00]">No weight on file</div>
          )}
        </Card>
        <Card className="p-4">
          <div className="text-[10px] font-bold uppercase text-[#7B6157]">Latest Blood Pressure</div>
          {data.latest_bp ? (
            <>
              <div className="font-heading text-2xl font-bold text-[#3E2723]">
                {data.latest_bp.systolic}/{data.latest_bp.diastolic}
              </div>
              <div className="text-xs text-[#7B6157]">mmHg</div>
              <div className="text-[10px] text-[#7B6157] mt-1">Recorded {dayjs(data.latest_bp.date).format("MMM D, YYYY")}</div>
            </>
          ) : (
            <div className="text-xs italic text-[#8D5A00]">No BP on file</div>
          )}
        </Card>
        <Card className="p-4">
          <div className="text-[10px] font-bold uppercase text-[#7B6157]">Latest HbA1c</div>
          {data.latest_a1c ? (
            <>
              <div className="font-heading text-2xl font-bold text-[#3E2723]">{data.latest_a1c.value}</div>
              <div className="text-[10px] text-[#7B6157] mt-1">Resulted {dayjs(data.latest_a1c.date).format("MMM D, YYYY")}</div>
            </>
          ) : (
            <div className="text-xs italic text-[#8D5A00]">No A1c on file</div>
          )}
        </Card>
        <Card className="p-4">
          <div className="text-[10px] font-bold uppercase text-[#7B6157]">Latest LDL</div>
          {data.latest_ldl ? (
            <>
              <div className="font-heading text-2xl font-bold text-[#3E2723]">{data.latest_ldl.value}</div>
              <div className="text-[10px] text-[#7B6157] mt-1">Resulted {dayjs(data.latest_ldl.date).format("MMM D, YYYY")}</div>
            </>
          ) : (
            <div className="text-xs italic text-[#8D5A00]">No LDL on file</div>
          )}
        </Card>
      </div>

      {data.overdue?.length > 0 && (
        <Card className="p-4 border border-[#B26A00] bg-[#FFF8E1]" data-testid="metabolic-overdue">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-bold text-[#8D5A00]">
              <AlertTriangle size={14} /> {data.overdue.length} overdue metric{data.overdue.length === 1 ? "" : "s"}
            </div>
            <Btn data-testid="metabolic-autoschedule-btn" onClick={autoschedule} disabled={scheduling}>
              <CalendarPlus size={13} /> {scheduling ? "Scheduling…" : "Schedule missing metrics"}
            </Btn>
          </div>
          <ul className="space-y-1 text-sm">
            {data.overdue.map((o) => (
              <li key={o.metric} className="text-[#5D4037]">
                <strong className="text-[#3E2723]">{o.metric}:</strong> {o.recommendation}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {data.overdue?.length === 0 && data.on_watchlist && (
        <Card className="p-4 border border-[#2E7D32] bg-[#E8F5E9]">
          <div className="flex items-center gap-2 text-sm font-bold text-[#2E7D32]">
            <CheckCircle2 size={14} /> All required metrics are within cadence
          </div>
        </Card>
      )}
    </div>
  );
}
